import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { organizationAPI, reportAPI, exportAPI, employeeAPI, retentionPaymentAPI, bonusPaymentAPI } from '@/lib/api';
import { formatCurrency, formatDate, getYearOptions, getCurrentYear, getMonthOptions } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  FileSpreadsheet,
  Loader2,
  Download,
  Wallet,
  Gift,
  Building2,
  Plus,
  Trash2,
  Lock,
  CheckCircle,
  Banknote,
} from 'lucide-react';

export default function ReportsPage() {
  const { isAdminLevel, canMarkPaid, canFinalize } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter state
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterYear, setFilterYear] = useState(getCurrentYear().toString());
  
  // Report data
  const [retentionData, setRetentionData] = useState([]);
  const [bonusData, setBonusData] = useState([]);
  
  // Payment records
  const [retentionPayments, setRetentionPayments] = useState([]);
  const [bonusPayments, setBonusPayments] = useState([]);
  
  // Dialogs
  const [addRetentionDialogOpen, setAddRetentionDialogOpen] = useState(false);
  const [addBonusDialogOpen, setAddBonusDialogOpen] = useState(false);
  const [confirmFinalizeId, setConfirmFinalizeId] = useState(null);
  const [confirmPaidId, setConfirmPaidId] = useState(null);
  const [paymentType, setPaymentType] = useState(null); // 'retention' or 'bonus'
  
  // Form state
  const [retentionForm, setRetentionForm] = useState({
    employee_id: '',
    period_start_month: '1',
    period_start_year: getCurrentYear().toString(),
    period_end_month: '6',
    period_end_year: getCurrentYear().toString(),
    total_retention: '',
  });
  
  const [bonusForm, setBonusForm] = useState({
    employee_id: '',
    year: getCurrentYear().toString(),
    bonus_amount: '',
  });
  
  const [submitting, setSubmitting] = useState(false);
  
  const yearOptions = getYearOptions();
  const monthOptions = getMonthOptions();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (organizations.length > 0) {
      loadPaymentRecords();
    }
  }, [filterOrg, filterYear]);

  const loadInitialData = async () => {
    try {
      const [orgRes, empRes] = await Promise.all([
        organizationAPI.getAll(),
        employeeAPI.getAll(),
      ]);
      setOrganizations(orgRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const loadPaymentRecords = async () => {
    setLoading(true);
    try {
      const params = { year: parseInt(filterYear) };
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      
      const [retRes, bonusRes] = await Promise.all([
        retentionPaymentAPI.getAll(params),
        bonusPaymentAPI.getAll(params),
      ]);
      setRetentionPayments(retRes.data);
      setBonusPayments(bonusRes.data);
    } catch (error) {
      console.error('Failed to load payment records:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRetentionReport = async () => {
    setLoading(true);
    try {
      const params = { year: parseInt(filterYear) };
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      
      const response = await reportAPI.getRetention(params);
      setRetentionData(response.data);
    } catch (error) {
      toast.error('Failed to load retention report');
    } finally {
      setLoading(false);
    }
  };

  const loadBonusReport = async () => {
    setLoading(true);
    try {
      const params = { year: parseInt(filterYear) };
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      
      const response = await reportAPI.getAnnualBonus(params);
      setBonusData(response.data);
    } catch (error) {
      toast.error('Failed to load bonus report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportRetention = () => {
    const params = { year: filterYear };
    if (filterOrg !== 'all') params.organization_id = filterOrg;
    
    const token = localStorage.getItem('paytrack_token');
    window.open(`${exportAPI.getRetentionReportUrl(params)}&authorization=Bearer ${token}`, '_blank');
  };

  const handleExportBonus = () => {
    const params = { year: filterYear };
    if (filterOrg !== 'all') params.organization_id = filterOrg;
    
    const token = localStorage.getItem('paytrack_token');
    window.open(`${exportAPI.getAnnualBonusReportUrl(params)}&authorization=Bearer ${token}`, '_blank');
  };

  // Retention Payment handlers
  const handleAddRetentionPayment = async () => {
    setSubmitting(true);
    try {
      await retentionPaymentAPI.create({
        employee_id: retentionForm.employee_id,
        period_start_month: parseInt(retentionForm.period_start_month),
        period_start_year: parseInt(retentionForm.period_start_year),
        period_end_month: parseInt(retentionForm.period_end_month),
        period_end_year: parseInt(retentionForm.period_end_year),
        total_retention: parseFloat(retentionForm.total_retention),
      });
      toast.success('Retention payment record created');
      setAddRetentionDialogOpen(false);
      setRetentionForm({
        employee_id: '',
        period_start_month: '1',
        period_start_year: getCurrentYear().toString(),
        period_end_month: '6',
        period_end_year: getCurrentYear().toString(),
        total_retention: '',
      });
      loadPaymentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    } finally {
      setSubmitting(false);
    }
  };

  // Bonus Payment handlers
  const handleAddBonusPayment = async () => {
    setSubmitting(true);
    try {
      await bonusPaymentAPI.create({
        employee_id: bonusForm.employee_id,
        year: parseInt(bonusForm.year),
        bonus_amount: parseFloat(bonusForm.bonus_amount),
      });
      toast.success('Bonus payment record created');
      setAddBonusDialogOpen(false);
      setBonusForm({
        employee_id: '',
        year: getCurrentYear().toString(),
        bonus_amount: '',
      });
      loadPaymentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalize = async () => {
    try {
      if (paymentType === 'retention') {
        await retentionPaymentAPI.finalize(confirmFinalizeId);
      } else {
        await bonusPaymentAPI.finalize(confirmFinalizeId);
      }
      toast.success('Payment finalized');
      setConfirmFinalizeId(null);
      setPaymentType(null);
      loadPaymentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize');
    }
  };

  const handleMarkPaid = async () => {
    try {
      if (paymentType === 'retention') {
        await retentionPaymentAPI.markPaid(confirmPaidId);
      } else {
        await bonusPaymentAPI.markPaid(confirmPaidId);
      }
      toast.success('Marked as paid');
      setConfirmPaidId(null);
      setPaymentType(null);
      loadPaymentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleDeleteRetentionPayment = async (id) => {
    try {
      await retentionPaymentAPI.delete(id);
      toast.success('Record deleted');
      loadPaymentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDeleteBonusPayment = async (id) => {
    try {
      await bonusPaymentAPI.delete(id);
      toast.success('Record deleted');
      loadPaymentRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusBadge = (record) => {
    if (record.is_paid) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
          <CheckCircle className="h-3 w-3" />
          Paid
        </span>
      );
    }
    if (record.is_finalized) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
          <Lock className="h-3 w-3" />
          Finalized
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
        Pending
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div data-testid="reports-page" className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports & Payments</h1>
          <p className="text-muted-foreground mt-1">
            Generate reports and manage retention & bonus payments
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg">
          <Select value={filterOrg} onValueChange={setFilterOrg}>
            <SelectTrigger className="w-[200px]" data-testid="report-filter-org">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[120px]" data-testid="report-filter-year">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Report Tabs */}
        <Tabs defaultValue="retention-payments" className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:w-[600px]">
            <TabsTrigger value="retention-payments" className="flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              <span className="hidden sm:inline">Retention</span> Pay
            </TabsTrigger>
            <TabsTrigger value="bonus-payments" className="flex items-center gap-2">
              <Gift className="h-4 w-4" />
              <span className="hidden sm:inline">Bonus</span> Pay
            </TabsTrigger>
            <TabsTrigger value="retention-report" className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Retention
            </TabsTrigger>
            <TabsTrigger value="bonus-report" className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Bonus
            </TabsTrigger>
          </TabsList>

          {/* Retention Payments Tab */}
          <TabsContent value="retention-payments" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Retention Payments</CardTitle>
                  <CardDescription>
                    Manage 6-monthly retention bonus payments
                  </CardDescription>
                </div>
                {isAdminLevel() && (
                  <Button onClick={() => setAddRetentionDialogOpen(true)} data-testid="add-retention-payment-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Payment
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-8 w-8 spinner" />
                  </div>
                ) : retentionPayments.length === 0 ? (
                  <div className="text-center py-12">
                    <Wallet className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No retention payment records for {filterYear}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Organization</TableHead>
                          <TableHead>Period</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Finalized</TableHead>
                          <TableHead className="text-center">Paid</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {retentionPayments.map((payment) => (
                          <TableRow key={payment.id} data-testid={`retention-payment-row-${payment.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{payment.employee_name}</p>
                                <p className="text-xs text-muted-foreground font-mono">{payment.employee_code}</p>
                              </div>
                            </TableCell>
                            <TableCell>{payment.organization_name}</TableCell>
                            <TableCell className="text-sm">
                              {payment.period_start_month}/{payment.period_start_year} - {payment.period_end_month}/{payment.period_end_year}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-amber-600 dark:text-amber-400">
                              {formatCurrency(payment.total_retention)}
                            </TableCell>
                            <TableCell className="text-center">{getStatusBadge(payment)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Checkbox
                                          checked={payment.is_finalized}
                                          disabled={payment.is_finalized || !canFinalize()}
                                          onCheckedChange={() => {
                                            setConfirmFinalizeId(payment.id);
                                            setPaymentType('retention');
                                          }}
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {payment.is_finalized ? `Finalized: ${formatDate(payment.finalized_at)}` : 'Click to finalize'}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {payment.finalized_at && (
                                  <span className="text-[10px] text-muted-foreground">{formatDate(payment.finalized_at)}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Checkbox
                                          checked={payment.is_paid}
                                          disabled={payment.is_paid || !payment.is_finalized || !canMarkPaid()}
                                          onCheckedChange={() => {
                                            setConfirmPaidId(payment.id);
                                            setPaymentType('retention');
                                          }}
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {payment.is_paid ? `Paid: ${formatDate(payment.paid_at)}` : 'Click to mark paid'}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {payment.paid_at && (
                                  <span className="text-[10px] text-muted-foreground">{formatDate(payment.paid_at)}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {isAdminLevel() && !payment.is_paid && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteRetentionPayment(payment.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bonus Payments Tab */}
          <TabsContent value="bonus-payments" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Annual Bonus Payments</CardTitle>
                  <CardDescription>
                    Manage yearly bonus payments
                  </CardDescription>
                </div>
                {isAdminLevel() && (
                  <Button onClick={() => setAddBonusDialogOpen(true)} data-testid="add-bonus-payment-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Payment
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-8 w-8 spinner" />
                  </div>
                ) : bonusPayments.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">No bonus payment records for {filterYear}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Organization</TableHead>
                          <TableHead>Year</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Finalized</TableHead>
                          <TableHead className="text-center">Paid</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bonusPayments.map((payment) => (
                          <TableRow key={payment.id} data-testid={`bonus-payment-row-${payment.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{payment.employee_name}</p>
                                <p className="text-xs text-muted-foreground font-mono">{payment.employee_code}</p>
                              </div>
                            </TableCell>
                            <TableCell>{payment.organization_name}</TableCell>
                            <TableCell>{payment.year}</TableCell>
                            <TableCell className="text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                              {formatCurrency(payment.bonus_amount)}
                            </TableCell>
                            <TableCell className="text-center">{getStatusBadge(payment)}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Checkbox
                                          checked={payment.is_finalized}
                                          disabled={payment.is_finalized || !canFinalize()}
                                          onCheckedChange={() => {
                                            setConfirmFinalizeId(payment.id);
                                            setPaymentType('bonus');
                                          }}
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {payment.is_finalized ? `Finalized: ${formatDate(payment.finalized_at)}` : 'Click to finalize'}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {payment.finalized_at && (
                                  <span className="text-[10px] text-muted-foreground">{formatDate(payment.finalized_at)}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <Checkbox
                                          checked={payment.is_paid}
                                          disabled={payment.is_paid || !payment.is_finalized || !canMarkPaid()}
                                          onCheckedChange={() => {
                                            setConfirmPaidId(payment.id);
                                            setPaymentType('bonus');
                                          }}
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {payment.is_paid ? `Paid: ${formatDate(payment.paid_at)}` : 'Click to mark paid'}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {payment.paid_at && (
                                  <span className="text-[10px] text-muted-foreground">{formatDate(payment.paid_at)}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {isAdminLevel() && !payment.is_paid && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteBonusPayment(payment.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Retention Report Tab */}
          <TabsContent value="retention-report" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Retention Report</CardTitle>
                  <CardDescription>
                    Employee-wise retention amount deducted monthly for {filterYear}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={loadRetentionReport} disabled={loading} data-testid="load-retention-btn">
                    {loading ? <Loader2 className="h-4 w-4 spinner mr-2" /> : null}
                    Load Report
                  </Button>
                  <Button variant="outline" onClick={handleExportRetention} disabled={retentionData.length === 0}>
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {retentionData.length === 0 ? (
                  <div className="text-center py-12">
                    <Wallet className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Click "Load Report" to view retention data
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee ID</TableHead>
                          <TableHead>Employee Name</TableHead>
                          <TableHead>Organization</TableHead>
                          <TableHead className="text-right">Monthly Retention</TableHead>
                          <TableHead className="text-right">Total Retained ({filterYear})</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {retentionData.map((item, index) => (
                          <TableRow key={index} data-testid={`retention-row-${index}`}>
                            <TableCell className="font-mono">{item.employee_id}</TableCell>
                            <TableCell className="font-medium">{item.employee_name}</TableCell>
                            <TableCell>{item.organization}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600 dark:text-amber-400">
                              {formatCurrency(item.monthly_retention)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold">
                              {formatCurrency(item.total_retained)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <div className="mt-4 p-4 bg-muted/30 rounded-lg flex justify-between items-center">
                      <span className="font-medium">Total Retention Held</span>
                      <span className="text-2xl font-bold font-mono text-amber-600 dark:text-amber-400">
                        {formatCurrency(retentionData.reduce((sum, item) => sum + item.total_retained, 0))}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Annual Bonus Report Tab */}
          <TabsContent value="bonus-report" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Annual Bonus Report</CardTitle>
                  <CardDescription>
                    Employee-wise annual bonus amounts
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={loadBonusReport} disabled={loading} data-testid="load-bonus-btn">
                    {loading ? <Loader2 className="h-4 w-4 spinner mr-2" /> : null}
                    Load Report
                  </Button>
                  <Button variant="outline" onClick={handleExportBonus} disabled={bonusData.length === 0}>
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {bonusData.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Click "Load Report" to view annual bonus data
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee ID</TableHead>
                          <TableHead>Employee Name</TableHead>
                          <TableHead>Organization</TableHead>
                          <TableHead>Vertical</TableHead>
                          <TableHead className="text-right">Gross Salary</TableHead>
                          <TableHead className="text-right">Annual Bonus</TableHead>
                          <TableHead>Joining Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bonusData.map((item, index) => (
                          <TableRow key={index} data-testid={`bonus-row-${index}`}>
                            <TableCell className="font-mono">{item.employee_id}</TableCell>
                            <TableCell className="font-medium">{item.employee_name}</TableCell>
                            <TableCell>{item.organization}</TableCell>
                            <TableCell>{item.vertical}</TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(item.gross_salary)}
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                              {item.annual_bonus > 0 ? formatCurrency(item.annual_bonus) : '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {item.joining_date || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <div className="mt-4 p-4 bg-muted/30 rounded-lg flex justify-between items-center">
                      <span className="font-medium">Total Annual Bonus</span>
                      <span className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(bonusData.reduce((sum, item) => sum + (item.annual_bonus || 0), 0))}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Retention Payment Dialog */}
        <Dialog open={addRetentionDialogOpen} onOpenChange={setAddRetentionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Retention Payment</DialogTitle>
              <DialogDescription>
                Create a retention payment record for an employee
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select value={retentionForm.employee_id} onValueChange={(v) => setRetentionForm({...retentionForm, employee_id: v})}>
                  <SelectTrigger data-testid="retention-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Period Start</Label>
                  <div className="flex gap-2">
                    <Select value={retentionForm.period_start_month} onValueChange={(v) => setRetentionForm({...retentionForm, period_start_month: v})}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map((m) => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label.substring(0,3)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={retentionForm.period_start_year} onValueChange={(v) => setRetentionForm({...retentionForm, period_start_year: v})}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Period End</Label>
                  <div className="flex gap-2">
                    <Select value={retentionForm.period_end_month} onValueChange={(v) => setRetentionForm({...retentionForm, period_end_month: v})}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map((m) => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label.substring(0,3)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={retentionForm.period_end_year} onValueChange={(v) => setRetentionForm({...retentionForm, period_end_year: v})}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="total_retention">Total Retention Amount</Label>
                <Input
                  id="total_retention"
                  type="number"
                  placeholder="30000"
                  value={retentionForm.total_retention}
                  onChange={(e) => setRetentionForm({...retentionForm, total_retention: e.target.value})}
                  data-testid="retention-amount-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddRetentionDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleAddRetentionPayment} 
                disabled={submitting || !retentionForm.employee_id || !retentionForm.total_retention}
                data-testid="save-retention-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 spinner mr-2" /> : null}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Bonus Payment Dialog */}
        <Dialog open={addBonusDialogOpen} onOpenChange={setAddBonusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Bonus Payment</DialogTitle>
              <DialogDescription>
                Create an annual bonus payment record for an employee
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select value={bonusForm.employee_id} onValueChange={(v) => setBonusForm({...bonusForm, employee_id: v})}>
                  <SelectTrigger data-testid="bonus-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={bonusForm.year} onValueChange={(v) => setBonusForm({...bonusForm, year: v})}>
                  <SelectTrigger data-testid="bonus-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bonus_amount">Bonus Amount</Label>
                <Input
                  id="bonus_amount"
                  type="number"
                  placeholder="50000"
                  value={bonusForm.bonus_amount}
                  onChange={(e) => setBonusForm({...bonusForm, bonus_amount: e.target.value})}
                  data-testid="bonus-amount-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddBonusDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleAddBonusPayment} 
                disabled={submitting || !bonusForm.employee_id || !bonusForm.bonus_amount}
                data-testid="save-bonus-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 spinner mr-2" /> : null}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Finalize Dialog */}
        <AlertDialog open={!!confirmFinalizeId} onOpenChange={() => { setConfirmFinalizeId(null); setPaymentType(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Finalize Payment</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to finalize this payment? This will lock the record.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleFinalize}>Finalize</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirm Paid Dialog */}
        <AlertDialog open={!!confirmPaidId} onOpenChange={() => { setConfirmPaidId(null); setPaymentType(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Mark as Paid</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to mark this payment as paid? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleMarkPaid} className="bg-emerald-600 hover:bg-emerald-700">
                <Banknote className="mr-2 h-4 w-4" />
                Mark as Paid
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
